<script>
import debounce from 'lodash/debounce';
import utilsMixin from '@shared/mixins/utils';
import APIStore from '@app/services/API/Store';
import CustomerTrainingTransactions from '@app/components/CustomerTrainingTransactions.vue';
import EventBus from '@shared/services/EventBus';

const FILTERS_MAP = {
  q: 'q',
  sort_by: 'sort',
  order_by: 'order',
  page: 'page',
  training_uuid: 'uuid',
};

export default {
  name: 'Sales',
  mixins: [utilsMixin],
  head: {
    title: 'Ventes',
  },
  data() {
    return {
      isExporting: false,
      params: this.getQueryParams(),
      sales: { data: [], links: {}, meta: {} },
    };
  },
  computed: {
    store() {
      return this.$store.getters['auth/store'];
    },
  },
  watch: {
    '$route.query': {
      handler() {
        this.params = this.getQueryParams();
        this.fetchResults();
      },
    },
  },
  methods: {
    resolveProductType(row) {
      if (row.membership_uuid) {
        return 'Abonnement';
      }
      if (row.training_uuid) {
        return 'Formation';
      }
      return 'Pack';
    },
    resolveProductName(row) {
      if (row.membership_uuid) {
        return row.membership_name;
      }
      if (row.training_uuid) {
        return row.training_name;
      }
      return row.bundle_name;
    },
    getQueryParams() {
      const { query } = this.$route;
      return {
        q: query[FILTERS_MAP.q] || '',
        sort_by: query[FILTERS_MAP.sort_by] || 'created_at',
        order_by: query[FILTERS_MAP.order_by] || 'desc',
        page: query[FILTERS_MAP.page] || 1,
      };
    },
    search(clearSortColumn = false) {
      if (clearSortColumn) {
        this.$refs.table.currentSortColumn = {};
      }

      const params = { ...this.params };
      params.q === '' && delete params.q;
      params.sort_by === '' && delete params.sort_by;

      this.$router.replace({
        query: {
          [FILTERS_MAP.q]: params.q,
          [FILTERS_MAP.sort_by]: params.sort_by,
          [FILTERS_MAP.order_by]: params.order_by,
          [FILTERS_MAP.page]: params.page,
        },
      });
    },
    fetchResults() {
      if (this.fetchPromise) {
        this.fetchPromise.cancel();
        this.fetchPromise = null;
      }

      const params = { ...this.params };
      params.q === '' && delete params.q;
      params.sort_by === '' && delete params.sort_by;

      const loader = this.$buefy.loading.open({ container: this.$refs.box });
      this.fetchPromise = APIStore.getSales(this.store.uuid, params)
        .then((data) => (this.sales = data))
        .finally(() => loader.close());

      return this.fetchPromise;
    },
    onSearch: debounce(function(value) {
      this.params.q = value.trim();
      this.search();
    }, 300),
    onSort(sort, order) {
      this.params.sort_by = sort;
      this.params.order_by = order;
      this.search();
    },
    onPageChange(page) {
      this.params.page = page;
      window.scroll(0, 0);
      this.search();
    },
    toggleOrder() {
      this.params.order_by = this.params.order_by !== 'desc' ? 'desc' : 'asc';
      this.search(true);
    },
    exportCSV() {
      this.isExporting = true;
      APIStore.exportSales(this.$store.getters['auth/store'].uuid)
        .then(() => this.$buefy.dialog.alert(`
          Un export CSV listant TOUTES vos ventes
          vous a été envoyé par email.
        `))
        .finally(() => (this.isExporting = false));
    },
    showTransactionDetails(row) {
      const loader = this.$buefy.loading.open();
      APIStore.getSaleCustomerData(this.store.uuid, row.customer_uuid, row.bundle_uuid, row.training_uuid)
        .then((data) => {
          const customer = {
            uuid: row.customer_uuid,
            firstname: row.customer_firstname,
            lastname: row.customer_lastname,
            email: row.customer_email,
          };

          const training = {
            uuid: row.training_uuid || row.bundle_uuid,
            name: row.training_uuid ? row.training_name : row.bundle_name,
          };

          const entityType = row.bundle_uuid ? 'bundle' : 'training';

          const transaction = data.transaction || {};

          if (!transaction.children) {
            transaction.children = [];
          }

          if (data.transaction && data.transaction.children && Array.isArray(data.transaction.children)) {
            transaction.children = data.transaction.children.map((child) => ({
              ...child,
              children: child.children || [], // S'assurer que chaque enfant a aussi une propriété children
            }));
          }

          const transactions = [transaction];

          this.$buefy.modal.open({
            parent: this,
            width: '768px',
            component: CustomerTrainingTransactions,
            props: {
              customer,
              training,
              transactions,
              entityType,
            },
          });
        })
        .catch((error) => {
          this.$buefy.toast.open({
            message: 'Erreur lors de la récupération des données de transaction',
            type: 'is-danger',
          });
          console.error('Erreur API getSaleCustomerData:', error);
        })
        .finally(() => {
          loader.close();
        });
    },
    handleCustomerPageReload() {
      this.fetchResults();
    },
  },
  mounted() {
    this.search();
    EventBus.on('customer-page-reload', this.handleCustomerPageReload);
  },
  beforeDestroy() {
    EventBus.off('customer-page-reload', this.handleCustomerPageReload);
  },
};
</script>

<template>
  <div class="scomp">
    <div class="level is-mobile">
      <div class="level-left">
        <h1 class="title is-size-4-mobile">
          Ventes
        </h1>
      </div>
      <div class="level-right">
        <b-tooltip label="Compatible Excel &amp; Google Sheet" position="is-left">
          <b-button
            type="is-primary"
            icon-left="file-export"
            @click="exportCSV"
            :loading="isExporting">
            Export CSV
          </b-button>
        </b-tooltip>
      </div>
    </div>

    <div class="columns is-hidden-mobile">
      <div class="column">
        <b-field label="Filtrer par">
          <b-input placeholder="email, prénom, nom, formation ou pack" maxlength="64" expanded @input="onSearch" />
        </b-field>
      </div>
      <div class="column is-narrow">
        <b-field label="Trier par">
          <b-select v-model="params.sort_by" @input="search(true)">
            <option value="email">Email</option>
            <option value="firstname">Prénom</option>
            <option value="lastname">Nom</option>
            <option value="training_name">Produit acheté</option>
            <option value="created_at">Date d'achat</option>
            <option value="remaining_price">Reste à payer</option>
            <option value="total_price">Montant total</option>
          </b-select>
          <p class="control">
            <b-tooltip :label="params.order_by == 'asc' ? 'croissant' : 'décroissant'">
              <b-button
                :icon-left="params.order_by == 'asc' ? 'arrow-up' : 'arrow-down'"
                @click="toggleOrder"
              />
            </b-tooltip>
          </p>
        </b-field>
      </div>
    </div>

    <div class="mb-3 level is-align-items-flex-end">
      <div class="level-left">
        <p v-if="sales.meta.total != null" class="has-text-right">
          Résultat :
          <strong>
            {{ sales.meta.total }} vente{{ sales.meta.total > 1 ? 's' : '' }}
          </strong>
        </p>
      </div>
    </div>

    <div ref="box" class="box is-relative mxw-full">
      <b-table
        ref="table"
        :data="sales.data"
        :total="sales.meta.total"
        :per-page="sales.meta.per_page"
        backend-sorting
        paginated
        backend-pagination
        @sort="onSort"
        @page-change="onPageChange">
        <b-table-column cell-class="py-5 is-size-8" v-slot="{ row }" field="created_at" label="Date" sortable>
          <b-tooltip :label="$moment.utc(row.created_at).local().format('DD/MM/YY [à] HH:mm')">
            {{ row.created_at | momentFromUTC | moment('from') }}
          </b-tooltip>
        </b-table-column>
        <b-table-column cell-class="scomp_smalltd py-5" v-slot="{ row }" field="firstname" label="Acheteur" sortable>
          <div>
            {{ row.customer_firstname }} {{ row.customer_lastname }} <br>
            <strong>{{ row.customer_email }}</strong>
          </div>
        </b-table-column>
        <b-table-column cell-class="scomp_smalltd py-5 break-words" v-slot="{ row }" field="training_name" label="Produit acheté" sortable>
          <div>
            {{ resolveProductType(row) }}
            <span
              v-if="row.membership_interval"
              class="is-size-7 has-text-black ml-1">
              · {{ formatIntervalShort({ interval: row.membership_interval, interval_count: row.membership_interval_count }) }}
            </span>
            <br>
            <strong>{{ resolveProductName(row) }}</strong>
          </div>
        </b-table-column>
        <b-table-column cell-class="scomp_smalltd py-5 break-words" v-slot="{ row }" field="remaining_price" label="Reste à payer" sortable numeric>
          <div>
            <span class="mr-2">{{ row.remaining_price | formatPrice }}</span>
            <b-tooltip
              v-if="row.remaining_price > 0"
              label="Voir les détails des transactions"
              position="is-top">
              <b-icon
                icon="eye"
                size="is-small"
                class="has-text-info is-clickable"
                @click.native="showTransactionDetails(row)"
              />
            </b-tooltip>
          </div>
        </b-table-column>
        <b-table-column cell-class="scomp_smalltd py-5 break-words" v-slot="{ row }" field="total_price" label="Montant payé" sortable numeric>
          <div>
            <b>{{ row.total_price | formatPrice }}</b>
            <br>
            <span class="is-size-8">
              <em v-if="row.payment_method_used == 'STRIPE'">Par Carte</em>
              <em v-else-if="row.payment_method_used == 'PAYPAL'">Par PayPal</em>
            </span>
          </div>
        </b-table-column>

        <template #empty>
          <p class="p-5 has-text-centered">
            Il n'y pas de résultat pour cette recherche
          </p>
        </template>
      </b-table>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.scomp {
  ::v-deep &_smalltd {
    max-width: 200px;
    word-wrap: break-word !important;
    word-break: break-word !important;
  }

  @include mobile {
    ::v-deep &_smalltd {
      max-width: none;
    }
  }
}
</style>
